'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { Loader2, Calendar, Clock, MapPin, Pencil, BadgeCheck, ShieldCheck, Plane, Home, AlertTriangle, ClipboardCheck, CalendarDays, List, UserSearch } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { AttendanceOverrideModal } from './AttendanceOverrideModal'
import { AdminSelfAttendance } from './AdminSelfAttendance'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { formatTime } from '@/lib/utils'
import { classifyDay } from '@/lib/attendance-classify'
import { formatISTDate, todayIST } from '@/lib/date-utils'
import { PageShell } from '@/components/ui/page-shell'
import { SectionHeader } from '@/components/ui/section-header'
import { GlassCard } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill, statusTone } from '@/components/ui/status-pill'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'
import { AttendanceCalendar, type CalendarDay } from '@/components/ui/attendance-calendar'
import { cn } from '@/lib/utils'

interface User {
  id: string
  name: string
  employeeId: string | null
}

interface BreakEntry {
  start: string | null
  end: string | null
}

interface AttendanceRecord {
  id: string
  date: string
  checkIn: string | null
  checkOut: string | null
  status: string
  workMode: string
  notes: string | null
  breaks?: BreakEntry[]
  user: User
}

function totalBreakMinutes(breaks?: BreakEntry[]): number {
  if (!Array.isArray(breaks)) return 0
  let t = 0
  for (const b of breaks) {
    if (b.start && b.end) {
      const [sh, sm] = b.start.split(':').map(Number)
      const [eh, em] = b.end.split(':').map(Number)
      const d = (eh * 60 + em) - (sh * 60 + sm)
      if (d > 0) t += d
    }
  }
  return t
}

interface AttendanceViewProps {
  currentUserId?: string
}

export function AttendanceView({ currentUserId }: AttendanceViewProps = {}) {
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([])
  const [users, setUsers] = useState<User[]>([])
  // Headcount from the user directory, independent of the month/employee filters
  // that scope the attendance table below.
  const [directory, setDirectory] = useState<{ active: number; admins: number }>({ active: 0, admins: 0 })
  const [leaveDates, setLeaveDates] = useState<Record<string, Set<string>>>({})
  const [holidayDates, setHolidayDates] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [filterUser, setFilterUser] = useState<string>('all')
  // Scopes the fetch to one month. Without it the view pulled the entire
  // attendance collection for all users for all time on every load.
  const [filterMonth, setFilterMonth] = useState<string>(() => todayIST().slice(0, 7))
  const [view, setView] = useState<'table' | 'calendar'>('table')
  const [overrideTarget, setOverrideTarget] = useState<{ userId: string; userName?: string; date: string; checkIn: string | null; checkOut: string | null; breaks: BreakEntry[] } | null>(null)

  const [manualActionOpen, setManualActionOpen] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])
  const [actionType, setActionType] = useState<'checkin' | 'checkout'>('checkin')
  const [timeHour, setTimeHour] = useState('9')
  const [timeMinute, setTimeMinute] = useState('00')
  const [timePeriod, setTimePeriod] = useState('AM')
  const [manualWorkMode, setManualWorkMode] = useState('OFFICE')
  const [manualReason, setManualReason] = useState('')
  const [submittingManual, setSubmittingManual] = useState(false)

  const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1))
  const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))

  const to24 = (hour: string, minute: string, period: string) => {
    let h = Number(hour) % 12
    if (period === 'PM') h += 12
    return `${String(h).padStart(2, '0')}:${minute}`
  }

  const handleManualSubmit = async () => {
    if (!selectedUserId) {
      toast.error('Select an employee')
      return
    }
    setSubmittingManual(true)
    try {
      const timeStr = to24(timeHour, timeMinute, timePeriod)
      const body: any = {
        userId: selectedUserId,
        date: selectedDate,
        reason: manualReason,
      }
      if (actionType === 'checkin') {
        body.checkIn = timeStr
        body.workMode = manualWorkMode
        body.status = 'PRESENT'
      } else {
        body.checkOut = timeStr
      }

      await apiFetch('/api/attendance/override', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })

      toast.success(actionType === 'checkin' ? 'Checked in successfully' : 'Checked out successfully')
      setManualActionOpen(false)
      fetchData()
    } catch (e: any) {
      toast.error(e.message || 'Failed to submit attendance')
    } finally {
      setSubmittingManual(false)
    }
  }

  const attendanceQuery = useMemo(() => {
    const params = new URLSearchParams()
    if (filterUser && filterUser !== 'all') params.set('userId', filterUser)
    if (filterMonth) {
      const [y, m] = filterMonth.split('-').map(Number)
      const lastDay = new Date(y, m, 0).getDate()
      params.set('startDate', `${filterMonth}-01`)
      params.set('endDate', `${filterMonth}-${String(lastDay).padStart(2, '0')}`)
    }
    return params.toString()
  }, [filterUser, filterMonth])

  const fetchData = useCallback(async () => {
    try {
      // Fetched independently: a failure in a supporting endpoint (leaves,
      // holidays) must not blank the attendance table itself.
      const asJson = async (p: Promise<Response>) => (await p).json()
      const [attendanceSettled, usersSettled, leavesSettled, holidaysSettled] = await Promise.allSettled([
        asJson(apiFetch(`/api/attendance?${attendanceQuery}`)),
        asJson(apiFetch('/api/users')),
        asJson(apiFetch('/api/leave')),
        asJson(apiFetch('/api/holidays'))
      ])

      if (attendanceSettled.status === 'rejected') {
        console.error('Error fetching attendance:', attendanceSettled.reason)
        toast.error(attendanceSettled.reason?.message || 'Failed to fetch attendance data')
      }
      for (const [label, r] of [['users', usersSettled], ['leaves', leavesSettled], ['holidays', holidaysSettled]] as const) {
        if (r.status === 'rejected') console.error(`Error fetching ${label}:`, r.reason)
      }

      const attendanceData = attendanceSettled.status === 'fulfilled' ? attendanceSettled.value : {}
      const usersData = usersSettled.status === 'fulfilled' ? usersSettled.value : {}
      const leavesData = leavesSettled.status === 'fulfilled' ? leavesSettled.value : {}
      const holidaysData = holidaysSettled.status === 'fulfilled' ? holidaysSettled.value : {}

      if (attendanceData.attendance) setAttendance(attendanceData.attendance)
      if (usersData.users) {
        setDirectory({
          active: usersData.users.filter((u: any) => u && u.isActive !== false).length,
          admins: usersData.users.filter((u: any) => u && (u.role === 'ADMIN' || u.role === 'HR')).length,
        })
        const showInactive = localStorage.getItem('sasha_show_inactive_users') === 'true'
        // No role filter: HR and admin staff have attendance records too, and
        // Admin/HR must be able to correct any user's day from this screen.
        setUsers(usersData.users.filter((u: any) => u && (showInactive || u.isActive !== false)))
      }

      if (holidaysData.holidays) {
        const hset = new Set<string>()
        for (const h of holidaysData.holidays) {
          if (h?.date) hset.add(h.date)
        }
        setHolidayDates(hset)
      }

      if (leavesData.leaves) {
        const map: Record<string, Set<string>> = {}
        for (const leave of leavesData.leaves) {
          if (leave.status !== 'APPROVED') continue
          const uid = leave.userId
          if (!map[uid]) map[uid] = new Set()
          const start = new Date(leave.startDate + 'T00:00:00')
          const end = new Date(leave.endDate + 'T00:00:00')
          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            map[uid].add(formatISTDate(d))
          }
        }
        setLeaveDates(map)
      }
    } catch (error: any) {
      // Per-endpoint failures are reported above; this catches unexpected errors
      // while processing the responses.
      console.error('Error processing attendance data:', error)
      toast.error(error?.message || 'Failed to load attendance view')
    } finally {
      setLoading(false)
    }
  }, [attendanceQuery])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const getDisplayStatus = useCallback((record: AttendanceRecord) => {
    const userLeaves = (record.user && leaveDates[record.user.id]) || new Set<string>()
    const cls = classifyDay({
      date: record.date,
      record: record.checkIn
        ? { checkIn: record.checkIn, checkOut: record.checkOut, workMode: record.workMode, breaks: record.breaks }
        : null,
      leaveDates: userLeaves,
      wfhDates: new Set<string>(),
      holidayDates,
      statusOverride: record.status ?? null,
    })
    return cls.status
  }, [leaveDates, holidayDates])

  const kpis = useMemo(() => {
    let leave = 0, wfh = 0, incomplete = 0
    for (const r of attendance) {
      const s = getDisplayStatus(r)
      if (s === 'LEAVE') leave++
      else if (s === 'WFH_NO_LOG') wfh++
      if (r.checkIn && !r.checkOut) incomplete++
    }
    return { leave, wfh, incomplete }
  }, [attendance, getDisplayStatus])

  const selectedUser = useMemo(
    () => (filterUser === 'all' ? null : users.find(u => u.id === filterUser) || null),
    [users, filterUser]
  )

  // The calendar shows one employee's month. Aggregating every employee into a
  // single day cell would lose the per-day status this view is about, so the
  // grid asks for an employee filter instead.
  const calendarDays = useMemo(() => {
    if (filterUser === 'all') return {}
    const byDate: Record<string, AttendanceRecord> = {}
    for (const r of attendance) {
      if (r.user?.id === filterUser) byDate[r.date] = r
    }
    const userLeaves = leaveDates[filterUser] || new Set<string>()
    const [y, m] = filterMonth.split('-').map(Number)
    const daysInMonth = new Date(y, m, 0).getDate()
    const today = todayIST()
    const out: Record<string, CalendarDay> = {}
    for (let d = 1; d <= daysInMonth; d++) {
      const date = `${filterMonth}-${String(d).padStart(2, '0')}`
      const rec = byDate[date]
      const cls = classifyDay({
        date,
        record: rec?.checkIn
          ? { checkIn: rec.checkIn, checkOut: rec.checkOut, workMode: rec.workMode, breaks: rec.breaks }
          : null,
        leaveDates: userLeaves,
        wfhDates: new Set<string>(),
        holidayDates,
        statusOverride: rec?.status ?? null,
      })
      // Days that haven't happened yet aren't absences — leave them unmapped so
      // the calendar renders them as future placeholders.
      if (date > today && cls.status === 'ABSENT') continue
      out[date] = {
        date,
        status: cls.status,
        hours: cls.hours,
        checkIn: cls.checkIn,
        checkOut: cls.checkOut,
        workMode: cls.workMode,
        incomplete: cls.incomplete,
      }
    }
    return out
  }, [attendance, filterUser, filterMonth, leaveDates, holidayDates])

  const openOverrideForDate = useCallback((date: string) => {
    if (filterUser === 'all') return
    const rec = attendance.find(r => r.date === date && r.user?.id === filterUser)
    setOverrideTarget({
      userId: filterUser,
      userName: selectedUser?.name,
      date,
      checkIn: rec?.checkIn ?? null,
      checkOut: rec?.checkOut ?? null,
      breaks: rec?.breaks || [],
    })
  }, [attendance, filterUser, selectedUser])

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00')
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = d.getFullYear()
    return `${day}/${month}/${year}`
  }

  const workModeTone = (m: string) => m === 'OFFICE' ? 'blue' : m === 'WFH' ? 'violet' : m === 'HYBRID' ? 'cyan' : 'neutral'

  return (
    <PageShell>
      <SectionHeader
        icon={ClipboardCheck}
        title="Attendance Records"
        description="View employee attendance history"
        action={
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="inline-flex shrink-0 rounded-xl bg-neutral-100 p-0.5 ring-1 ring-inset ring-neutral-200 dark:bg-white/5 dark:ring-white/10">
              {([['table', 'Table', List], ['calendar', 'Calendar', CalendarDays]] as const).map(([v, label, Icon]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  aria-pressed={view === v}
                  className={cn(
                    'inline-flex flex-1 items-center justify-center gap-1.5 rounded-[10px] px-3 py-1.5 text-xs font-medium transition-colors',
                    view === v
                      ? 'bg-white text-neutral-900 shadow-sm dark:bg-white/10 dark:text-white'
                      : 'text-neutral-500 hover:text-neutral-800 dark:text-white/50 dark:hover:text-white/80'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>
            <Button onClick={() => setManualActionOpen(true)} className={PRIMARY_BTN}>
              Check In/Out Employee
            </Button>
            <Input
              type="month"
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              aria-label="Attendance month"
              className="w-full sm:w-[170px]"
            />
            <Select value={filterUser} onValueChange={setFilterUser}>
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {currentUserId && <AdminSelfAttendance userId={currentUserId} />}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiTile label="Active" value={directory.active} icon={BadgeCheck} tone="green" hint="Active user accounts" />
        <KpiTile label="Admins" value={directory.admins} icon={ShieldCheck} tone="violet" hint="Admin & HR accounts" />
        <KpiTile label="On Leave" value={kpis.leave} icon={Plane} tone="amber" />
        <KpiTile label="On WFH" value={kpis.wfh} icon={Home} tone="violet" />
        <KpiTile label="Incomplete" value={kpis.incomplete} icon={AlertTriangle} tone="orange" />
      </div>

      <GlassCard>
        {loading ? (
          <TabLoader />
        ) : view === 'calendar' ? (
          filterUser === 'all' ? (
            <EmptyState
              icon={UserSearch}
              title="Select an employee to see their calendar"
              description="The month grid shows one person's day-by-day status. Pick an employee from the filter above."
            />
          ) : (
            <AttendanceCalendar
              month={filterMonth}
              onMonthChange={setFilterMonth}
              days={calendarDays}
              onDayClick={(d) => openOverrideForDate(d.date)}
              hint={`Click any day to correct ${selectedUser?.name || 'this employee'}'s check-in, check-out or breaks.`}
            />
          )
        ) : attendance.length === 0 ? (
          <EmptyState
            icon={ClipboardCheck}
            title="No attendance records to display"
            description="Try a different month or employee filter."
          />
        ) : (
          <>
            <div className="hidden md:block">
              <ScrollArea className="h-[520px]">
                <div className="min-w-[860px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-neutral-200 dark:border-white/10 hover:bg-transparent">
                        <TableHead className="text-neutral-500 dark:text-white/50">Employee</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Date</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Check In</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Check Out</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Status</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Break</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Work Mode</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Work Done</TableHead>
                        <TableHead className="text-neutral-500 dark:text-white/50">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attendance.map((record) => {
                        const status = getDisplayStatus(record)
                        return (
                          <TableRow key={record.id} className="border-neutral-100 dark:border-white/5 hover:bg-neutral-50 dark:bg-white/5">
                            <TableCell>
                              <p className="text-neutral-900 dark:text-white/90 font-medium">{record.user?.name}</p>
                              <p className="text-xs text-neutral-400 dark:text-white/40">{record.user?.employeeId}</p>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5 text-neutral-800 dark:text-white/80 text-sm">
                                <Calendar className="h-3.5 w-3.5 text-neutral-400 dark:text-white/40" />
                                {formatDate(record.date)}
                              </div>
                            </TableCell>
                            <TableCell>
                              {record.checkIn ? (
                                <div className="flex items-center gap-1 text-neutral-800 dark:text-white/80">
                                  <Clock className="h-4 w-4 text-neutral-400 dark:text-white/40" />
                                  {formatTime(record.checkIn)}
                                </div>
                              ) : (
                                <span className="text-neutral-300 dark:text-white/30">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {record.checkIn ? (
                                record.checkOut ? (
                                  <div className="flex items-center gap-1 text-neutral-800 dark:text-white/80">
                                    <Clock className="h-4 w-4 text-neutral-400 dark:text-white/40" />
                                    {formatTime(record.checkOut)}
                                    {(record as any).overrideApplied && (
                                      <StatusPill tone="blue" className="ml-1">FIXED</StatusPill>
                                    )}
                                  </div>
                                ) : (
                                  <StatusPill tone="orange">INCOMPLETE</StatusPill>
                                )
                              ) : (
                                <span className="text-neutral-300 dark:text-white/30">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <StatusPill tone={statusTone(status)} dot>{status}</StatusPill>
                            </TableCell>
                            <TableCell>
                              {record.checkIn ? (
                                <div className="text-xs">
                                  {(record.breaks || []).length === 0 ? (
                                    <span className="text-neutral-300 dark:text-white/30">none</span>
                                  ) : (
                                    <>
                                      {(record.breaks || []).map((b, i) => (
                                        <div key={i} className="text-neutral-700 dark:text-white/70">
                                          {b.start ? formatTime(b.start) : '-'}–{b.end ? formatTime(b.end) : <span className="text-amber-600 dark:text-amber-400">in progress</span>}
                                        </div>
                                      ))}
                                      <div className="text-neutral-400 dark:text-white/40 mt-1">Total: {totalBreakMinutes(record.breaks)} min</div>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <span className="text-neutral-300 dark:text-white/30">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {record.checkIn ? (
                                <div className="flex items-center gap-1.5">
                                  <MapPin className="h-3.5 w-3.5 text-neutral-400 dark:text-white/40" />
                                  <StatusPill tone={workModeTone(record.workMode)}>{record.workMode}</StatusPill>
                                </div>
                              ) : (
                                <span className="text-neutral-300 dark:text-white/30">-</span>
                              )}
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate" title={record.notes || ''}>
                              {record.notes || <span className="text-neutral-300 dark:text-white/30">—</span>}
                            </TableCell>
                            <TableCell>
                              {record.user && (
                                <Button size="icon" variant="ghost" className="text-neutral-600 dark:text-white/60 hover:text-neutral-900 dark:hover:text-white" onClick={() => setOverrideTarget({ userId: record.user!.id, userName: record.user!.name, date: record.date, checkIn: record.checkIn, checkOut: record.checkOut, breaks: record.breaks || [] })}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              </ScrollArea>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {attendance.map((record) => {
                const status = getDisplayStatus(record)
                return (
                  <GlassCard key={record.id} tone={statusTone(status) === 'green' ? 'green' : statusTone(status) === 'red' ? 'red' : statusTone(status) === 'amber' ? 'amber' : statusTone(status) === 'violet' ? 'violet' : 'default'}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-neutral-900 dark:text-white/90 font-medium truncate">{record.user?.name}</p>
                        <p className="text-xs text-neutral-400 dark:text-white/40">{record.user?.employeeId} · {formatDate(record.date)}</p>
                      </div>
                      <StatusPill tone={statusTone(status)} dot>{status}</StatusPill>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-neutral-400 dark:text-white/40">Check In</p>
                        <p className="text-neutral-800 dark:text-white/80">{record.checkIn ? formatTime(record.checkIn) : '—'}</p>
                      </div>
                      <div>
                        <p className="text-neutral-400 dark:text-white/40">Check Out</p>
                        <p className="text-neutral-800 dark:text-white/80">{record.checkOut ? formatTime(record.checkOut) : record.checkIn ? 'incomplete' : '—'}</p>
                      </div>
                      {record.checkIn && (
                        <div className="col-span-2 flex items-center gap-2 mt-1">
                          <StatusPill tone={workModeTone(record.workMode)}>{record.workMode}</StatusPill>
                          {(record.breaks || []).length > 0 && (
                            <span className="text-neutral-400 dark:text-white/40">Break: {totalBreakMinutes(record.breaks)} min</span>
                          )}
                        </div>
                      )}
                    </div>
                    {record.notes && (
                      <div className="mt-2 text-xs text-neutral-600 dark:text-white/60 bg-neutral-100/50 dark:bg-white/[0.01] rounded-lg p-2">
                        <span className="font-medium text-neutral-700 dark:text-white/70">Work Done: </span>{record.notes}
                      </div>
                    )}
                    {record.user && (
                      <div className="mt-3 flex justify-end">
                        <Button size="sm" variant="ghost" className="text-orange-600 dark:text-orange-300 hover:text-orange-700 dark:hover:text-orange-200" onClick={() => setOverrideTarget({ userId: record.user!.id, userName: record.user!.name, date: record.date, checkIn: record.checkIn, checkOut: record.checkOut, breaks: record.breaks || [] })}>
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                      </div>
                    )}
                  </GlassCard>
                )
              })}
            </div>
          </>
        )}
      </GlassCard>

      {overrideTarget && (
        <AttendanceOverrideModal
          open={true}
          onOpenChange={(o) => !o && setOverrideTarget(null)}
          userId={overrideTarget.userId}
          userName={overrideTarget.userName}
          date={overrideTarget.date}
          currentCheckIn={overrideTarget.checkIn}
          currentCheckOut={overrideTarget.checkOut}
          currentBreaks={overrideTarget.breaks}
          onSaved={() => { setOverrideTarget(null); fetchData() }}
        />
      )}

      <Dialog open={manualActionOpen} onOpenChange={setManualActionOpen}>
        <DialogContent className="bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-white/10 max-w-md w-full text-neutral-900 dark:text-neutral-100">
          <DialogHeader>
            <DialogTitle className="text-neutral-900 dark:text-white/90">Manual Attendance Check In/Out</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-white/50">Employee</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="w-full bg-transparent border-neutral-200 dark:border-white/10">
                  <SelectValue placeholder="Select Employee" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-neutral-950 border-neutral-200 dark:border-white/10">
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-white/50">Date</Label>
                <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="bg-transparent border-neutral-200 dark:border-white/10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-white/50">Action</Label>
                <Select value={actionType} onValueChange={(v: any) => setActionType(v)}>
                  <SelectTrigger className="w-full bg-transparent border-neutral-200 dark:border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-neutral-950 border-neutral-200 dark:border-white/10">
                    <SelectItem value="checkin">Check In</SelectItem>
                    <SelectItem value="checkout">Check Out</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-white/50">Time</Label>
              <div className="flex gap-1.5">
                <Select value={timeHour} onValueChange={setTimeHour}>
                  <SelectTrigger className="w-full bg-transparent border-neutral-200 dark:border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white dark:bg-neutral-950 border-neutral-200 dark:border-white/10">{HOURS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={timeMinute} onValueChange={setTimeMinute}>
                  <SelectTrigger className="w-full bg-transparent border-neutral-200 dark:border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white dark:bg-neutral-950 border-neutral-200 dark:border-white/10">{MINUTES.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={timePeriod} onValueChange={setTimePeriod}>
                  <SelectTrigger className="w-full bg-transparent border-neutral-200 dark:border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white dark:bg-neutral-950 border-neutral-200 dark:border-white/10">
                    <SelectItem value="AM">AM</SelectItem>
                    <SelectItem value="PM">PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {actionType === 'checkin' && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-white/50">Work Mode</Label>
                <Select value={manualWorkMode} onValueChange={setManualWorkMode}>
                  <SelectTrigger className="w-full bg-transparent border-neutral-200 dark:border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-neutral-950 border-neutral-200 dark:border-white/10">
                    <SelectItem value="OFFICE">Office</SelectItem>
                    <SelectItem value="WFH">WFH</SelectItem>
                    <SelectItem value="HYBRID">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-white/50">Reason / Notes</Label>
              <Textarea value={manualReason} onChange={e => setManualReason(e.target.value)} placeholder="e.g. Forgot to check in, power outage..." className="bg-transparent border-neutral-200 dark:border-white/10" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualActionOpen(false)} className="border-neutral-200 dark:border-white/10">Cancel</Button>
            <Button onClick={handleManualSubmit} disabled={submittingManual} className={PRIMARY_BTN}>
              {submittingManual ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  )
}
